import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { shiftApi } from '../../../api/shiftApi';
import { toast } from 'react-toastify';

type ShiftFormProps = {
  onSuccess?: () => void;
  showCard?: boolean;
  showTitle?: boolean;
};

const ShiftForm: React.FC<ShiftFormProps> = ({ onSuccess, showCard = true, showTitle = true }) => {
  const [shiftName, setShiftName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [breakDuration, setBreakDuration] = useState('60');
  const [gracePeriod, setGracePeriod] = useState('15');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shiftName || !startTime || !endTime) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await shiftApi.createShift({
        shiftName,
        startTime,
        endTime,
        breakDuration: Number(breakDuration),
        gracePeriod: Number(gracePeriod),
      });
      toast.success('Shift saved successfully!');
      setShiftName('');
      setStartTime('');
      setEndTime('');
      setBreakDuration('60');
      setGracePeriod('15');
      onSuccess?.();
    } catch (err: any) {
      const responseData = err.response?.data;
      const modelErrors = responseData?.errors
        ? Object.values(responseData.errors).flat().join(', ')
        : '';
      toast.error(responseData?.message || modelErrors || 'Failed to save shift');
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <>
      {showTitle && (
        <Typography variant="h6" fontWeight="bold" gutterBottom >
          Add New Shift
        </Typography>
      )}
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Shift Name *"
              fullWidth
              size="small"
              value={shiftName}
              onChange={(e) => setShiftName(e.target.value)}
              placeholder="e.g., Morning Shift, Night Shift"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              label="Start Time *"
              type="time"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              label="End Time *"
              type="time"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              label="Break Duration (minutes)"
              type="number"
              fullWidth
              size="small"
              value={breakDuration}
              onChange={(e) => setBreakDuration(e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              label="Grace Period (minutes)"
              type="number"
              fullWidth
              size="small"
              value={gracePeriod}
              onChange={(e) => setGracePeriod(e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Save />}
          sx={{ mt: 3 ,textTransform: 'none'}}
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Save Shift'}
        </Button>
      </Box>
    </>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card>
      <CardContent>{content}</CardContent>
    </Card>
  );
};

export default ShiftForm;
